import { Box, Button, CircularProgress, Stack } from '@mui/material';
import { DownloadIcon, LinkIcon, PrinterIcon } from 'lucide-react';

import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { useCopiedTooltip } from '@modules/_shared/hooks/useCopiedTooltip';
import { useDocumentUrl } from '@modules/_shared/hooks/useDocumentUrl';
import {
  useReportDownloadHandler,
  useReportPrintHandler,
  useTrackedDocumentLink,
} from '@modules/_shared/utils/reportHandlers';
import { useLocale } from '@shared/hooks/locale';

const StepShareReport = () => {
  const { t } = useLocale();
  const {
    documentId,
    documentType,
    templateId,
    language,
    activeIframeRef,
    renderFilename,
  } = useReportContext();
  const {
    showCopiedTooltip: copied,
    onCopied,
    resetCopied,
  } = useCopiedTooltip();
  const documentUrl = useDocumentUrl(templateId, documentId, language);

  const { copyLink, isMinting, copiedWithoutTracking, resetCopyWarning } =
    useTrackedDocumentLink(documentUrl, documentType);

  const handleDownload = useReportDownloadHandler(documentUrl);

  const handlePrint = useReportPrintHandler(activeIframeRef);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
        }}
      >
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Button
            variant="contained"
            startIcon={
              isMinting ? <CircularProgress size={16} /> : <LinkIcon />
            }
            onClick={() => copyLink(onCopied)}
            fullWidth
          >
            {t('Copy link')}
          </Button>

          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload(renderFilename)}
            fullWidth
          >
            {t('Download as PDF')}
          </Button>

          <Button
            variant="contained"
            startIcon={<PrinterIcon />}
            onClick={handlePrint}
            fullWidth
          >
            {t('Print the report')}
          </Button>
        </Stack>
      </Box>
      {copied && !copiedWithoutTracking && (
        <RASnackbar
          message={t('Link copied to clipboard')}
          onClose={resetCopied}
          severity="success"
        />
      )}
      {copiedWithoutTracking && (
        <RASnackbar
          message={t('Link copied without tracking')}
          onClose={resetCopyWarning}
          severity="warning"
        />
      )}
    </Box>
  );
};

export default StepShareReport;
